import dayjs from 'dayjs';

/**
 * Converts a JavaScript Date into a Firestore-like timestamp object.
 *
 * @param {Date | null | undefined} date
 * @returns {{seconds: number, nanoseconds: number}|undefined|null}
 *
 * @example
 * toFirestoreTimestamp(new Date('2025-10-04T00:00:00Z'));
 * // { seconds: 1756936800, nanoseconds: 0 }
 */
export function toFirestoreTimestamp(date) {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;

    return {
        seconds: Math.floor(date.getTime() / 1000),
        nanoseconds: 0
    };
}

/**
 * Converts a Firestore Timestamp (or Firestore-like timestamp object) into a JavaScript `Date`.
 *
 * Supports:
 * - Firestore `Timestamp` instances (anything with a `.toDate()` function)
 * - Plain objects shaped like `{ seconds, nanoseconds }`
 * - Plain objects shaped like `{ _seconds, _nanoseconds }` (common in serialized data)
 * - Date-like input values (ISO string / number / Date), as a last resort
 *
 * Notes:
 * - Returns `null` for `null`/`undefined`/invalid inputs.
 * - If both seconds+nanoseconds are provided, nanoseconds are converted to milliseconds
 *   (precision is limited to milliseconds in JS `Date`).
 *
 * @param {any} ts - Firestore Timestamp, Firestore-like object, or date-like value.
 * @returns {Date|null} A JavaScript `Date` instance, or `null` if conversion fails.
 *
 * @example
 * // Firestore Timestamp instance
 * firestoreTimestampToDate(doc.data().createdAt);
 *
 * @example
 * // Firestore-like object
 * firestoreTimestampToDate({ seconds: 1756936800, nanoseconds: 0 });
 * // -> Date for 2025-10-04T00:00:00.000Z
 *
 * @example
 * // Serialized variant
 * firestoreTimestampToDate({ _seconds: 1756936800, _nanoseconds: 0 });
 *
 * @example
 * // Fallback (date-like)
 * firestoreTimestampToDate("2025-10-04T00:00:00Z");
 */
export function firestoreTimestampToDate(ts) {
    if (!ts) return null;

    if (typeof ts?.toDate === 'function') return ts.toDate();

    if (typeof ts?.seconds === 'number') {
        const ms = ts.seconds * 1000 + Math.floor((ts.nanoseconds || 0) / 1_000_000);
        return new Date(ms);
    }

    if (typeof ts?._seconds === 'number') {
        const ms = ts._seconds * 1000 + Math.floor((ts._nanoseconds || 0) / 1_000_000);
        return new Date(ms);
    }

    const d = new Date(ts);
    return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Converts a Firestore-like timestamp object into an American date format (MM/DD/YYYY).
 *
 * Supports both Firestore Timestamp fields (`seconds`, `nanoseconds`)
 * and plain object variants (`_seconds`, `_nanoseconds`).
 *
 * @param {Object} timestamp - Object with seconds/nanoseconds properties.
 * @param {number} [timestamp.seconds] - Unix timestamp in seconds.
 * @param {number} [timestamp._seconds] - Alternative field name for seconds.
 * @param {number} [timestamp.nanoseconds] - Nanoseconds part (not used in formatting).
 * @param {number} [timestamp._nanoseconds] - Alternative field name for nanoseconds.
 * @param {boolean} [includeTime=false] - Whether to include time (HH:MM AM/PM).
 * @returns {string} Date string in MM/DD/YYYY format, optionally with time.
 *
 * @example
 * const rental_end = { nanoseconds: 0, seconds: 1756936800 };
 * console.log(convertFromFirestoreTimestamp(rental_end));
 * // "10/04/2025"
 *
 * console.log(convertFromFirestoreTimestamp(rental_end, true));
 * // "10/04/2025 12:00 AM"
 */
export function convertFromFirestoreTimestamp(timestamp, includeTime = false) {
    if (
        !timestamp ||
        (typeof timestamp.seconds !== 'number' && typeof timestamp._seconds !== 'number')
    ) {
        throw new Error('Invalid timestamp format');
    }

    const seconds = timestamp.seconds ?? timestamp._seconds;
    const date = new Date(seconds * 1000);

    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();

    let result = `${month}/${day}/${year}`;

    if (includeTime) {
        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12 || 12;
        result += ` ${hours}:${minutes} ${ampm}`;
    }

    return result;
}
